// Shared look for the concession report (jsPDF): brand colors, headers, KPI boxes, tables, text helpers.
// Mirrors the dashboard's design tokens so the PDF reads like the Overview page.
import { QUEUE_TIME_GOOD_MAX } from '../../concessionTheme';

export const MARGIN = 15;

export const C = {
  brand: [2, 166, 147],
  brandDark: [4, 109, 97],
  brandTint: [230, 246, 244],
  text: [26, 26, 26],
  muted: [91, 103, 101],
  subtle: [138, 150, 148],
  border: [226, 232, 230],
  surfaceMuted: [245, 248, 247],
  good: [22, 163, 74],
  goodTint: [232, 246, 237],
  warn: [217, 119, 6],
  warnTint: [253, 243, 230],
  bad: [220, 38, 38],
  badTint: [252, 235, 235],
  white: [255, 255, 255],
};

export const STATUS_RGB = { good: C.good, warn: C.warn, bad: C.bad, neutral: C.text };
export const TONE = {
  bad: { color: C.bad, tint: C.badTint },
  good: { color: C.good, tint: C.goodTint },
  info: { color: C.brand, tint: C.brandTint },
  warn: { color: C.warn, tint: C.warnTint },
};

export const hexToRgb = (hex = '#49AFA0') => {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

export const setFill = (pdf, rgb) => pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
export const setDraw = (pdf, rgb) => pdf.setDrawColor(rgb[0], rgb[1], rgb[2]);
export const setText = (pdf, rgb) => pdf.setTextColor(rgb[0], rgb[1], rgb[2]);

export const pageSize = (pdf) => ({
  width: pdf.internal.pageSize.getWidth(),
  height: pdf.internal.pageSize.getHeight(),
});

// Leaves room for the footer
export const ensureSpace = (pdf, y, needed) => {
  const { height } = pageSize(pdf);
  if (y + needed > height - MARGIN - 8) {
    pdf.addPage();
    return MARGIN + 4;
  }
  return y;
};

// Plain text from React nodes (findings are written as JSX for the dashboard)
export const toPlain = (node) => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(toPlain).join('');
  if (node.props) return toPlain(node.props.children);
  return '';
};

// Page title with a full-width brand underline; returns the next y
export const pageHeader = (pdf, title, subtitle) => {
  const { width } = pageSize(pdf);
  let y = MARGIN + 6;
  setText(pdf, C.brand);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.text('CONCESSIONS', MARGIN, y);
  y += 8;
  setText(pdf, C.text);
  pdf.setFontSize(20);
  pdf.text(title, MARGIN, y);
  y += 4;
  setDraw(pdf, C.brand);
  pdf.setLineWidth(0.8);
  pdf.line(MARGIN, y, width - MARGIN, y);
  y += 7;
  if (subtitle) {
    setText(pdf, C.muted);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    const lines = pdf.splitTextToSize(subtitle, width - MARGIN * 2);
    pdf.text(lines, MARGIN, y);
    y += lines.length * 4.6 + 3;
  }
  return y;
};

export const sectionLabel = (pdf, text, y) => {
  setText(pdf, C.muted);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.text(text.toUpperCase(), MARGIN, y);
  return y + 5;
};

// KPI box: label on top, big value, optional caption. Value colored by status.
export const kpiBox = (pdf, x, y, w, h, { label, value, caption, status = 'neutral', emphasis = false }) => {
  setFill(pdf, C.white);
  setDraw(pdf, C.border);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(x, y, w, h, 2.5, 2.5, 'FD');
  if (emphasis) {
    setFill(pdf, C.brand);
    pdf.rect(x + 0.15, y + 0.15, w - 0.3, 1.4, 'F');
  }
  setText(pdf, C.muted);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7.5);
  pdf.text(pdf.splitTextToSize(label.toUpperCase(), w - 6), x + 3, y + 7);
  setText(pdf, STATUS_RGB[status] || C.text);
  pdf.setFontSize(17);
  pdf.text(String(value), x + 3, y + 18);
  if (caption) {
    setText(pdf, C.subtle);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.text(pdf.splitTextToSize(caption, w - 6), x + 3, y + 23.5);
  }
};

// Small colored square (game color swatch)
export const swatch = (pdf, x, y, rgb, size = 2.8) => {
  setFill(pdf, rgb);
  pdf.roundedRect(x, y - size + 0.4, size, size, 0.6, 0.6, 'F');
};

// Simple table. columns: [{ header, width, align, render(row) -> string | { text, color, bold, swatch } }]
export const table = (pdf, y, columns, rows, { rowHeight = 8, headerHeight = 8, zebra = true } = {}) => {
  const { width: pageWidth } = pageSize(pdf);
  const totalWidth = pageWidth - MARGIN * 2;
  const fixed = columns.reduce((sum, c) => sum + (c.width || 0), 0);
  const flexCount = columns.filter((c) => !c.width).length || 1;
  const flexWidth = (totalWidth - fixed) / flexCount;
  const widths = columns.map((c) => c.width || flexWidth);

  const drawHeader = (yy) => {
    setFill(pdf, C.surfaceMuted);
    pdf.rect(MARGIN, yy, totalWidth, headerHeight, 'F');
    setText(pdf, C.muted);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7.5);
    let x = MARGIN;
    columns.forEach((c, i) => {
      const tx = c.align === 'right' ? x + widths[i] - 2.5 : c.align === 'center' ? x + widths[i] / 2 : x + 2.5;
      pdf.text(c.header.toUpperCase(), tx, yy + headerHeight / 2 + 1.3, { align: c.align || 'left' });
      x += widths[i];
    });
    return yy + headerHeight;
  };

  y = ensureSpace(pdf, y, headerHeight + rowHeight);
  y = drawHeader(y);

  rows.forEach((row, ri) => {
    if (y + rowHeight > pageSize(pdf).height - MARGIN - 8) {
      pdf.addPage();
      y = drawHeader(MARGIN + 4);
    }
    if (zebra && ri % 2 === 1) {
      setFill(pdf, [250, 252, 251]);
      pdf.rect(MARGIN, y, totalWidth, rowHeight, 'F');
    }
    setDraw(pdf, C.border);
    pdf.setLineWidth(0.2);
    pdf.line(MARGIN, y + rowHeight, MARGIN + totalWidth, y + rowHeight);

    let x = MARGIN;
    columns.forEach((c, i) => {
      const cell = c.render(row);
      const spec = typeof cell === 'object' && cell !== null ? cell : { text: cell };
      const text = spec.text === undefined || spec.text === null ? '' : String(spec.text);
      let tx = c.align === 'right' ? x + widths[i] - 2.5 : c.align === 'center' ? x + widths[i] / 2 : x + 2.5;
      const ty = y + rowHeight / 2 + 1.3;
      if (spec.swatch) {
        swatch(pdf, tx, ty, spec.swatch);
        tx += 4.2;
      }
      setText(pdf, spec.color || C.text);
      pdf.setFont('helvetica', spec.bold ? 'bold' : 'normal');
      pdf.setFontSize(spec.size || 8.5);
      pdf.text(text, tx, ty, { align: c.align || 'left' });
      if (spec.flag) {
        setText(pdf, C.bad);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7);
        const fx = c.align === 'center' ? tx + pdf.getTextWidth(text) / 2 + 1.5 : tx + pdf.getTextWidth(text) + 1.5;
        pdf.text('!', fx, ty - 1);
      }
      x += widths[i];
    });
    y += rowHeight;
  });
  return y;
};

export const queueStatus = (value, overflow = false) => (overflow || Math.round(value) >= QUEUE_TIME_GOOD_MAX ? 'bad' : 'good');
export const countStatus = (value, low, high) => (value <= low ? 'good' : value <= high ? 'warn' : 'bad');

const WINDOW_PRESETS = [
  { label: 'Full game', start: -180, end: 240 },
  { label: 'Before start', start: -180, end: 0 },
  { label: 'In-game', start: 0, end: 240 },
];
export const windowLabel = (timeRange) =>
  WINDOW_PRESETS.find((p) => p.start === timeRange.startOffset && p.end === timeRange.endOffset)?.label || 'Custom window';

export const formatOffset = (minutes) => {
  if (minutes === 0) return 'game start';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const text = h ? (m ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
  return minutes > 0 ? `+${text}` : `-${text}`;
};
